import { Button, Drawer, Grid2, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Container from '../components/Container'
import { AccountCircle, Assignment, CorporateFare, FilterFrames, Home, Logout, MedicalInformation, Menu, People, Person, SmartToy, SupportAgent, Translate } from '@mui/icons-material'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import SplashScreen from '../components/SplashScreen'
import { useAuth } from '../hooks/useAuth'
import sierraBackground from '/sierra-background.png'
import LangDropdown from '../components/LangDropdown'
import { routes } from '../router'
import { version } from '../../package.json'
import { useSnackbar } from 'notistack'
import { registerApiRequestCallback } from '../utils/axios'
import { useApp } from '../contexts/AppContext'
import { grey } from '@mui/material/colors'

export interface IRoute {
    path: string;
    title?: string;
    element: ReactNode;
    icon?: ReactNode;
}

const dashboardIcons = [<Person />, <FilterFrames />]
const dashboardTitles = ['Profile', 'Orders']

const CustomerDashboardLayout = () => {
    const { user, userLoaded, fetchUser, logout } = useAuth()
    const location = useLocation();
    const navigate = useNavigate()
    const { translate, lang } = useApp()

    const [drawerOpen, setDrawerOpen] = useState(false)

    const dashboardRoutes = useMemo(() => routes.map(o => o.path == '/dashboard' ? o.children : undefined).filter(i => i != undefined)[0], [routes])
    const sideBar = useMemo(() => dashboardRoutes.map((r, i) => dashboardIcons[i] ? ({ ...r, icon: dashboardIcons[i], title: dashboardTitles[i] ? dashboardTitles[i] : undefined }) : r) as IRoute[], [dashboardRoutes])

    const activePath = useCallback(
        (r: string) => {
            return location.pathname.split('/')[2] == `/dashboard${r != '' ? '/' + r : r}`.split('/')[2]
        }
        , [location])

    useEffect(() => {
        fetchUser()
    }, [])

    useEffect(() => {
        if (userLoaded && !user) navigate('/login?redirect=' + window.location.href)
    }, [user, userLoaded])

    const handleLogout = () => {
        setDrawerOpen(false)
        logout(() => navigate('/login'))
    }

    if (!user) return <SplashScreen />

    return (
        <Grid2 container size={'grow'} justifyContent={'center'} sx={{
            backgroundImage: `url(${sierraBackground})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scaleX(-1)'
        }}>

            <Grid2 sx={{ position: 'absolute', bottom: 0, right: 10, transform: 'scaleX(-1)' }}>
                <Typography color='primary.dark'>v{version}</Typography>
            </Grid2>

            <Grid2 container size={'grow'} maxWidth={1600} flexDirection={'column'} height={'100vh'} sx={{ transform: 'scaleX(-1)' }}>

                <Grid2 container size={'grow'} padding={2} spacing={2} flexDirection={{ xs: 'column', lg: 'row' }}>
                    <Grid2 container size={'auto'} flexDirection={'column'} display={{ xs: 'none', lg: 'flex' }}>

                        <Container size={'auto'} overflow={'visible'} display={'flex'} justifyContent={'center'}>
                            <LangDropdown />
                        </Container>

                        <Grid2 display={'flex'} justifyContent={'center'} sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
                            <img src='/sierra-logo.png' height={100} />
                        </Grid2>

                        <Container container >
                            <List disablePadding>
                                {
                                    sideBar && sideBar.map((r, i) =>
                                        <ListItem key={i} disablePadding sx={{
                                            color: (t) => activePath(r.path) ? t.palette.secondary.main : t.palette.primary.main,
                                            backgroundColor: (t) => activePath(r.path) ? t.palette.primary.main : 'transparent',

                                        }}>
                                            <ListItemButton onClick={() => navigate(r.path)}
                                                sx={{ paddingY: 2, paddingX: 6, }}>
                                                <ListItemIcon sx={{ color: (t) => activePath(r.path) ? t.palette.secondary.main : t.palette.primary.main }}>
                                                    {r.icon}
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <Typography fontWeight={'bold'}>
                                                        {r.title && translate(r.title) || translate(r.path)}
                                                    </Typography>
                                                </ListItemText>
                                            </ListItemButton>
                                        </ListItem>

                                    )
                                }

                                <ListItem disablePadding sx={{ color: 'green', justifyContent: 'center' }}>
                                    <ListItemButton onClick={() => navigate('/')}
                                        sx={{ paddingY: 2, paddingX: 6, }}>
                                        <ListItemIcon sx={{ color: 'green' }}>
                                            <Logout sx={{ transform: 'scale(-1)' }} />
                                        </ListItemIcon>
                                        <ListItemText primary={translate('Return Home')} />
                                    </ListItemButton>
                                </ListItem>

                                <ListItem disablePadding sx={{ color: 'red', justifyContent: 'center' }}>
                                    <ListItemButton onClick={handleLogout}
                                        sx={{ paddingY: 2, paddingX: 6, }}>
                                        <ListItemIcon sx={{ color: 'red' }}>
                                            <Logout />
                                        </ListItemIcon>
                                        <ListItemText primary={translate('Logout')} />
                                    </ListItemButton>
                                </ListItem>
                            </List>
                        </Container>
                    </Grid2>

                    <Grid2 container display={{ xs: 'flex', lg: 'none' }}>
                        <Grid2 size='auto' display={'flex'} alignItems={'end'}>
                            <IconButton sx={{ color: 'primary.contrastText' }} onClick={() => setDrawerOpen(!drawerOpen)}>
                                <Menu fontSize='large' />
                            </IconButton>
                        </Grid2>
                        <Grid2 size='grow' display={'flex'} justifyContent={'center'}>
                            <img src='/sierra-logo.png' style={{ height: 60 }} />
                        </Grid2>
                        <Grid2 size='auto' display={'flex'} alignItems={'end'} >
                            <IconButton color='error' onClick={handleLogout}>
                                <Logout fontSize='large' />
                            </IconButton>
                        </Grid2>
                    </Grid2>

                    <Grid2 container size={'grow'}>
                        <Outlet />
                    </Grid2>

                </Grid2>

            </Grid2>

            <Drawer anchor={lang === 'ar' ? 'right' : 'left'} open={drawerOpen} onClose={() => setDrawerOpen(false)}>

                <Grid2 container flexDirection={'column'} gap={2}>

                    <Grid2 size={'auto'} overflow={'visible'} display={'flex'} justifyContent={'center'} sx={{ backgroundColor: grey[200] }}>
                        <LangDropdown />
                    </Grid2>


                    <Grid2 display={'flex'} justifyContent={'center'} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                        <img src='/sierra-logo-dark.png' height={100} />
                    </Grid2>

                    <Grid2 container>
                        <List disablePadding>
                            {
                                sideBar && sideBar.map((r, i) =>
                                    <ListItem key={i} disablePadding sx={{
                                        color: (t) => activePath(r.path) ? t.palette.secondary.main : t.palette.primary.main,
                                        backgroundColor: (t) => activePath(r.path) ? t.palette.primary.main : 'transparent',

                                    }}>
                                        <ListItemButton onClick={() => {
                                            setDrawerOpen(false)
                                            navigate(r.path)
                                        }}
                                            sx={{ paddingY: 2, paddingX: 6, }}>
                                            <ListItemIcon sx={{ color: (t) => activePath(r.path) ? t.palette.secondary.main : t.palette.primary.main }}>
                                                {r.icon}
                                            </ListItemIcon>
                                            <ListItemText>
                                                <Typography fontWeight={'bold'}>
                                                    {r.title && translate(r.title) || translate(r.path)}
                                                </Typography>
                                            </ListItemText>
                                        </ListItemButton>
                                    </ListItem>

                                )
                            }

                            <ListItem disablePadding sx={{ color: 'green', justifyContent: 'center' }}>
                                <ListItemButton onClick={() => {
                                    setDrawerOpen(false)
                                    navigate('/')
                                }}
                                    sx={{ paddingY: 2, paddingX: 6, }}>
                                    <ListItemIcon sx={{ color: 'green' }}>
                                        <Logout sx={{ transform: 'scale(-1)' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={translate('Return Home')} />
                                </ListItemButton>
                            </ListItem>

                            <ListItem disablePadding sx={{ color: 'red', justifyContent: 'center' }}>
                                <ListItemButton onClick={handleLogout}
                                    sx={{ paddingY: 2, paddingX: 6, }}>
                                    <ListItemIcon sx={{ color: 'red' }}>
                                        <Logout />
                                    </ListItemIcon>
                                    <ListItemText primary={translate('Logout')} />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Grid2>
                </Grid2>
            </Drawer>
        </Grid2>
    )
}

export default CustomerDashboardLayout